// src/components/insurance/InsurancesMobileView.tsx
import React from 'react';
import { Edit, Trash2, Search, RefreshCw } from 'lucide-react';
import { InsuranceData } from '../../pages/Insurance';
import Pagination from '../shared/Pagination';

interface InsurancesMobileViewProps {
  paginatedData: InsuranceData[];
  apiLoading: boolean;
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEdit: (insurance: InsuranceData) => void;
  handleDelete: (id: string) => void;
  getLabel: (group: string, value: string) => string;
  formatCurrency: (value: string | number) => string;
  formatDate: (date: string) => string;
  paginationProps: any;
}

const InsuranceCard: React.FC<Omit<InsurancesMobileViewProps, 'paginatedData' | 'apiLoading' | 'searchTerm' | 'onSearchChange' | 'paginationProps'> & { insurance: InsuranceData }> = ({
    insurance, handleEdit, handleDelete, getLabel, formatCurrency, formatDate
}) => (
    <div className="bg-white rounded-lg p-3 shadow border border-gray-200 flex flex-col">
        <div className="flex justify-between items-start mb-2">
            <div className="text-pink-800 font-bold truncate text-sm">
                {getLabel('insuranceType', insurance.insuranceType) || 'Insurance'}
            </div>
            <div className="flex space-x-1">
                <button onClick={() => handleEdit(insurance)} className="p-1 text-pink-600"><Edit className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(insurance.id)} className="p-1 text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
        </div>
        <div className="mb-2">
            <p className="text-base font-bold text-pink-600 text-center truncate">{formatCurrency(insurance.totalInsuranceAmount)}</p>
        </div>
        <div className="space-y-1 text-xs text-gray-600 text-center">
            <div className="text-gray-800 font-medium truncate">{insurance.insuranceName}</div>
            <div className="text-gray-600">{getLabel('insuranceSubType', insurance.insuranceSubType)}</div>
            <div className="text-gray-600">{formatCurrency(insurance.insuranceInstallmentAmount)} / {getLabel('insuranceInstallmentFrequency', insurance.insuranceInstallmentFrequency)}</div>
            {insurance.insuranceEndDate && <div className="text-gray-600">End: {formatDate(insurance.insuranceEndDate)}</div>}
        </div>
    </div>
);

const InsurancesMobileView: React.FC<InsurancesMobileViewProps> = ({ paginatedData, apiLoading, searchTerm, onSearchChange, paginationProps, ...rest }) => {
    return (
        <div className="md:hidden">
            <div className="p-2">
                <div className="relative max-w-md mx-auto mb-4">
                    <input type="text" placeholder="Search policies..." value={searchTerm} onChange={onSearchChange} className="w-full p-2 pl-10 border rounded-lg text-sm" />
                    <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
            </div>
            {apiLoading ? (
                <div className="text-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-pink-600" /></div>
            ) : (
                <div className="grid grid-cols-2 gap-4 px-2">
                    {paginatedData.map(insurance => <InsuranceCard key={insurance.id} insurance={insurance} {...rest} />)}
                </div>
            )}
            {paginationProps.totalItems > paginationProps.itemsPerPage && !apiLoading && (
                <Pagination {...paginationProps} isMobile />
            )}
        </div>
    );
};

export default InsurancesMobileView;